import {
  DESTINATION_ORDER,
  describeProblems,
  destinationName,
  listNames,
  readSyncResults,
  type SyncProblem,
} from '../../utils/syncResults'
import { useState, useEffect, useRef } from 'react'
import { LABEL_INGREDIENTS_MAX } from '../../utils/ingredientStatement'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from '../../lib/shadcn/dialog'
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../lib/shadcn/tabs'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { Label } from '../../lib/shadcn/label'
import { Switch } from '../../lib/shadcn/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { Upload, ImageIcon, Sparkles, Layers } from 'lucide-react'
import ProductPhotoTile from './ProductPhotoTile'
import CombinePhotosDialog from './CombinePhotosDialog'
import {
  useUpsertProduct,
  useUploadProductPhoto,
  useGetAvailableSkus,
  useCleanupProductPhoto,
  useGetPhotoOriginals,
  useWriteProductDescriptions,
} from '../../hooks/backend/inventoryPage'
import {
  useGetProductOptions,
  useGetPhotoCleanupSettings,
  useGetProductDefaults,
} from '../../hooks/backend/settingsPage'
import MultiSelect from '../../components/MultiSelect'
import DatePicker from '../../components/DatePicker'
import { cn } from '../../lib/shadcn/utils'
import type { Product } from './ProductTable'
import { usePublishLabelProducts } from '../../hooks/backend/inventoryPage'
import { publishLabels } from '../../utils/publishLabels'
import {
  RecipeEditorFields,
  RecipeSaveConfirm,
  recipeDescription,
  useRecipeEditor,
} from './recipeEditor'
import RecipeCostSummary from './RecipeCostSummary'
import { HelpText } from '../../components/HelpText'

interface ProductFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  // null = add new; a Product = edit existing
  product: Product | null
  onSaved: () => void
  // Open on this tab (e.g. the Recipe
  // button opens straight to the recipe).
  initialTab?: Section
}

// ─────────────────────────────────────────────
// Publishing
// ─────────────────────────────────────────────

type PublishDestination =
  | 'zoho'
  | 'n8n'
  | 'b2b'
  | 'web'

const DEFAULT_PUBLISH_TO: PublishDestination[] = [
  'zoho',
  'n8n',
  'b2b',
  'web',
]

const PUBLISH_OPTIONS = [
  'Zoho',
  'Faire',
  'B2B',
  'Web',
]

const publishValueToLabel = (
  value: PublishDestination
): string => {
  switch (value) {
    case 'zoho':
      return 'Zoho'
    case 'n8n':
      return 'Faire'
    case 'b2b':
      return 'B2B'
    case 'web':
      return 'Web'
  }
}

const publishLabelToValue = (
  label: string
): PublishDestination => {
  switch (label) {
    case 'Zoho':
      return 'zoho'
    case 'B2B':
      return 'b2b'
    case 'Web':
      return 'web'
    default:
      return 'n8n'
  }
}

// ─────────────────────────────────────────────
// Size-specific fields
// ─────────────────────────────────────────────

interface SizeValues {
  sku: string
  item_name: string
  weight_oz: string
  actual_weight_oz: string
  price: string
  qty_in_stock: string
  qty_remake: string
}

const EMPTY_SIZE: SizeValues = {
  sku: '',
  item_name: '',
  weight_oz: '',
  actual_weight_oz: '',
  price: '',
  qty_in_stock: '0',
  qty_remake: '0',
}

// Settings > Products > New product defaults.
interface NewProductDefaults {
  photo_url: string | null
  small_price: number
  large_price: number
}

const BUILT_IN_DEFAULTS: NewProductDefaults = {
  photo_url: null,
  small_price: 5,
  large_price: 10,
}

// The default price for an item name that
// says its size ("... - Small"), else ''.
function defaultPriceFor(
  itemName: string,
  defaults: NewProductDefaults
): string {
  if (/\bsmall\b/i.test(itemName))
    return String(defaults.small_price)
  if (/\blarge\b/i.test(itemName))
    return String(defaults.large_price)
  return ''
}

// ─────────────────────────────────────────────
// Shared fields
// ─────────────────────────────────────────────

type SharedState = {
  label_name: string
  // Short bag description (labels, Zoho).
  description: string
  // Long description for the online stores.
  web_description: string
  ingredients: string
  allergens: string

  // Ordered list of photo URLs;
  // first entry is featured.
  photos: string[]

  categories: string[]
  tags: string[]

  // Publishing destinations shared by all sizes.
  publish_to: PublishDestination[]

  // Seasonal availability; null means never
  // set, so a save leaves the stores alone.
  is_seasonal: boolean | null
  available_start: string
  available_end: string

  // null = never set, '' = cleared.
  wholesale_tier: string | null

  // Unbagged popcorn on hand (oz), shared by
  // the sizes. Special Order flavors only.
  unbagged_oz: string
}

const NO_TIER = '__none__'

// Special Order flavors manage stock on the
// retail website (bagged stock + unbagged batch).
const isSpecialOrder = (tier: string | null) =>
  (tier ?? '').trim().toLowerCase() ===
  'special order'

// "Fills about 20 Small or 10 Large." for the
// unbagged batch, from each size's weight.
function batchFillText(
  ozText: string,
  sizes: { name: string; size: SizeValues }[]
): string {
  const oz = Number(ozText)
  const fills = sizes
    .map(({ name, size }) => {
      const bag = Number(
        size.actual_weight_oz || size.weight_oz
      )
      return bag > 0 && oz > 0
        ? `${Math.floor(oz / bag + 1e-9)} ${name}`
        : null
    })
    .filter(Boolean)
  const lead =
    'A made batch not bagged yet. The retail website shows bagged Qty In Stock plus what this fills, and orders of either size take from it.'
  return fills.length
    ? `${lead} Fills about ${fills.join(' or ')} now.`
    : lead
}

const EMPTY_SHARED: SharedState = {
  label_name: '',
  description: '',
  web_description: '',
  ingredients: '',
  allergens: '',
  photos: [],

  // Default category for new products.
  categories: ['Kettle Corn'],

  tags: [],

  // New products publish everywhere by default.
  publish_to: [...DEFAULT_PUBLISH_TO],

  is_seasonal: false,
  available_start: '',
  available_end: '',
  wholesale_tier: null,
  unbagged_oz: '',
}

function toShared(
  p: Product
): SharedState {
  const s = (v: unknown) =>
    v == null ? '' : String(v)

  // ───────────────────────────────────────────
  // Photos
  // ───────────────────────────────────────────

  const rawPhotos =
    p.photos

  const legacyImageUrl =
    p.image_url

  let photos: string[] =
    Array.isArray(rawPhotos)
      ? rawPhotos.filter(
          (u): u is string =>
            typeof u === 'string' &&
            u.length > 0
        )
      : []

  // Backward compatibility for products
  // that only have image_url.
  if (
    photos.length === 0 &&
    legacyImageUrl
  ) {
    photos = [
      legacyImageUrl,
    ]
  }

  // ───────────────────────────────────────────
  // Publish To
  // ───────────────────────────────────────────

  /*
   * Retool/Postgres may return publish_to
   * as either:
   *
   * ['zoho', 'b2b']
   *
   * or:
   *
   * {zoho,b2b}
   *
   * or occasionally a JSON string.
   *
   * Treat it as unknown first so TypeScript
   * can safely narrow the runtime value.
   */

  const rawPublishTo: unknown =
    p.publish_to

  let publishTo:
    PublishDestination[]

  if (
    Array.isArray(rawPublishTo)
  ) {
    publishTo =
      rawPublishTo.filter(
        (
          value
        ): value is PublishDestination =>
          value === 'zoho' ||
          value === 'n8n' ||
          value === 'b2b' ||
          value === 'web'
      )
  } else if (
    typeof rawPublishTo ===
    'string'
  ) {
    const trimmed =
      rawPublishTo.trim()

    // {"zoho","b2b"}, ["zoho","b2b"] or
    // ['zoho', 'b2b'] (older rows).
    const parsed: string[] =
      trimmed
        .replace(/^[[{]/, '')
        .replace(/[\]}]$/, '')
        .split(',')
        .map(value =>
          value
            .trim()
            .replace(
              /^["']|["']$/g,
              ''
            )
            .trim()
            .toLowerCase()
        )
        .filter(Boolean)

    publishTo =
      parsed.filter(
        (
          value
        ): value is PublishDestination =>
          value === 'zoho' ||
          value === 'n8n' ||
          value === 'b2b' ||
          value === 'web'
      )
  } else {
    /*
     * Only default to all four when
     * publish_to is absent/null.
     */
    publishTo = [
      ...DEFAULT_PUBLISH_TO,
    ]
  }

  return {
    label_name:
      s(p.label_name),

    description:
      s(p.description),

    web_description:
      s(p.web_description),

    ingredients:
      s(p.ingredients),

    allergens:
      s(p.allergens),

    photos,

    categories:
      Array.isArray(p.categories)
        ? p.categories
        : [],

    tags:
      Array.isArray(p.tags)
        ? p.tags
        : [],

    publish_to:
      publishTo,

    is_seasonal:
      p.is_seasonal ?? null,

    available_start:
      s(p.available_start).slice(0, 10),

    available_end:
      s(p.available_end).slice(0, 10),

    wholesale_tier:
      p.wholesale_tier ?? null,

    unbagged_oz:
      s(p.unbagged_oz),
  }
}

function toSize(p: Product): SizeValues {
  const s = (v: unknown) =>
    v == null ? '' : String(v)

  return {
    sku: s(p.sku),
    item_name: s(p.item_name),
    weight_oz: s(p.weight_oz),
    actual_weight_oz: s(p.actual_weight_oz),
    price: s(p.price),
    qty_in_stock: s(p.qty_in_stock),
    qty_remake: s(p.qty_remake),
  }
}

// ─────────────────────────────────────────────
// File helper
// ─────────────────────────────────────────────

function fileToBase64(
  file: File
): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      const result =
        reader.result as string

      const comma =
        result.indexOf(',')

      resolve(
        comma >= 0
          ? result.slice(comma + 1)
          : result
      )
    }

    reader.onerror = () =>
      reject(reader.error)

    reader.readAsDataURL(file)
  })
}

// ─────────────────────────────────────────────
// Size field group
// ─────────────────────────────────────────────

function SizeFieldGroup({
  idPrefix,
  title,
  part,
  values,
  onChange,
  skuDisabled,
  skuHelper,
}: {
  idPrefix: string
  title?: string
  // details = SKU, name and weights;
  // pricing = price and stock.
  part: 'details' | 'pricing'
  values: SizeValues
  onChange: (
    key: keyof SizeValues,
    value: string
  ) => void
  skuDisabled?: boolean
  skuHelper?: React.ReactNode
}) {
  const required = (
    <span className="text-destructive">
      *
    </span>
  )

  return (
    <div className="space-y-4">
      {title && (
        <h4 className="text-sm font-semibold text-foreground">
          {title}
        </h4>
      )}

      {part === 'details' ? (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-sku`}>
              SKU {required}
            </Label>

            <Input
              id={`${idPrefix}-sku`}
              value={values.sku}
              onChange={e =>
                onChange('sku', e.target.value)
              }
              disabled={skuDisabled}
              placeholder="Unique SKU"
              autoComplete="off"
            />

            {skuHelper}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-item-name`}>
              Item Name {required}
            </Label>

            <Input
              id={`${idPrefix}-item-name`}
              value={values.item_name}
              onChange={e =>
                onChange('item_name', e.target.value)
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-weight`}>
                Weight (oz) {required}
              </Label>

              <Input
                id={`${idPrefix}-weight`}
                type="number"
                step="0.01"
                min="0.01"
                value={values.weight_oz}
                onChange={e =>
                  onChange('weight_oz', e.target.value)
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-actual-weight`}>
                Actual Weight (oz)
              </Label>

              <Input
                id={`${idPrefix}-actual-weight`}
                type="number"
                step="0.01"
                min="0.01"
                placeholder={
                  values.weight_oz.trim()
                    ? `Same as Weight (${values.weight_oz})`
                    : 'Same as Weight'
                }
                value={values.actual_weight_oz}
                onChange={e =>
                  onChange(
                    'actual_weight_oz',
                    e.target.value
                  )
                }
              />
            </div>
          </div>

          <p className="text-xs text-muted-foreground"><HelpText>
            Actual Weight is what a bag really
            weighs on average. Used for production
            plans, costs and the B2B and Web store
            weight. Labels, Zoho and Faire keep
            Weight.
          </HelpText></p>
        </>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-price`}>
              Price {required}
            </Label>

            <Input
              id={`${idPrefix}-price`}
              type="number"
              step="0.01"
              min="0"
              value={values.price}
              onChange={e =>
                onChange('price', e.target.value)
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-qty`}>
                Qty In Stock
              </Label>

              <Input
                id={`${idPrefix}-qty`}
                type="number"
                min="0"
                value={values.qty_in_stock}
                onChange={e =>
                  onChange(
                    'qty_in_stock',
                    e.target.value
                  )
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`${idPrefix}-remake`}>
                Remake Qty
              </Label>

              <Input
                id={`${idPrefix}-remake`}
                type="number"
                min="0"
                value={values.qty_remake}
                onChange={e =>
                  onChange(
                    'qty_remake',
                    e.target.value
                  )
                }
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────
// Sections (tabs) and unsaved changes
// ─────────────────────────────────────────────

type Section =
  | 'details'
  | 'pricing'
  | 'recipe'
  | 'channels'

const SECTIONS: {
  value: Section
  label: string
}[] = [
  { value: 'details', label: 'Details' },
  { value: 'pricing', label: 'Pricing & Stock' },
  { value: 'recipe', label: 'Recipe' },
  { value: 'channels', label: 'Channels' },
]

// What the form looked like when it opened.
interface FormSnapshot {
  shared: SharedState
  size1: SizeValues
  size2: SizeValues
  pairMode: boolean
}

const SECTION_SHARED: Record<
  Section,
  (keyof SharedState)[]
> = {
  details: [
    'photos',
    'label_name',
    'description',
    'web_description',
    'ingredients',
    'allergens',
  ],
  pricing: ['wholesale_tier', 'unbagged_oz'],
  channels: [
    'publish_to',
    'categories',
    'tags',
    'is_seasonal',
    'available_start',
    'available_end',
  ],
  recipe: [],
}

const SECTION_SIZE: Record<
  Section,
  (keyof SizeValues)[]
> = {
  details: [
    'sku',
    'item_name',
    'weight_oz',
    'actual_weight_oz',
  ],
  pricing: [
    'price',
    'qty_in_stock',
    'qty_remake',
  ],
  recipe: [],
  channels: [],
}

// Compare values the way the save treats
// them: order doesn't matter for lists other
// than photos, and "never set" seasonal or
// tier counts the same as off / none.
function sameValue(
  key: keyof SharedState,
  a: unknown,
  b: unknown
): boolean {
  if (key === 'is_seasonal') {
    return (a === true) === (b === true)
  }
  if (key === 'wholesale_tier') {
    return (a || '') === (b || '')
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    const x = key === 'photos' ? a : [...a].sort()
    const y = key === 'photos' ? b : [...b].sort()
    return (
      x.length === y.length &&
      x.every((v, i) => v === y[i])
    )
  }
  return a === b
}

function changedSections(
  now: FormSnapshot,
  start: FormSnapshot | null
): Section[] {
  if (!start) return []
  return SECTIONS.map(s => s.value).filter(
    section => {
      if (
        section === 'details' &&
        now.pairMode !== start.pairMode
      ) {
        return true
      }
      const sharedChanged = SECTION_SHARED[
        section
      ].some(
        k =>
          !(
            k === 'available_start' ||
            k === 'available_end'
          ) || now.shared.is_seasonal
            ? !sameValue(
                k,
                now.shared[k],
                start.shared[k]
              )
            : false
      )
      const sizes: [SizeValues, SizeValues][] = [
        [now.size1, start.size1],
        ...(now.pairMode
          ? ([[now.size2, start.size2]] as [
              SizeValues,
              SizeValues,
            ][])
          : []),
      ]
      const sizeChanged = sizes.some(
        ([a, b]) =>
          SECTION_SIZE[section].some(
            k =>
              a[k].trim() !== b[k].trim()
          )
      )
      return sharedChanged || sizeChanged
    }
  )
}

// ─────────────────────────────────────────────
// Dialog
// ─────────────────────────────────────────────

export default function ProductFormDialog({
  open,
  onOpenChange,
  product,
  onSaved,
  initialTab = 'details',
}: ProductFormDialogProps) {
  const isEdit = product !== null

  const [shared, setShared] =
    useState<SharedState>(EMPTY_SHARED)

  const [size1, setSize1] =
    useState<SizeValues>(EMPTY_SIZE)

  const [size2, setSize2] =
    useState<SizeValues>(EMPTY_SIZE)

  const [pairMode, setPairMode] =
    useState(false)

  // Which section (tab) is showing.
  const [tab, setTab] =
    useState<Section>('details')

  // The form as it opened, to spot
  // unsaved changes.
  const [baseline, setBaseline] =
    useState<FormSnapshot | null>(null)

  // The recipe loads the first time the
  // Recipe or Pricing tab (its cost) is shown.
  const [recipeSeen, setRecipeSeen] =
    useState(false)
  const recipe = useRecipeEditor({
    product: isEdit ? product : null,
    active: open && isEdit && recipeSeen,
  })
  const publishLabelProducts =
    usePublishLabelProducts()
  // Saving the product and/or recipe.
  const [savingAll, setSavingAll] =
    useState(false)

  // "Discard unsaved changes?" prompt.
  const [confirmDiscard, setConfirmDiscard] =
    useState(false)

  const [
    suggestedSku2,
    setSuggestedSku2,
  ] = useState('')

  const fileInputRef =
    useRef<HTMLInputElement>(null)

  const upsertProduct =
    useUpsertProduct()

  const uploadProductPhoto =
    useUploadProductPhoto()

  // "Write with AI" descriptions.
  const writeDescriptions =
    useWriteProductDescriptions()
  const [aiOpen, setAiOpen] =
    useState(false)
  const [aiNotes, setAiNotes] =
    useState('')

  const runWriteDescriptions =
    async () => {
      if (!aiNotes.trim()) {
        toast.error(
          'Write a few words about the flavor first'
        )
        return
      }
      try {
        const res =
          (await writeDescriptions.trigger({
            label_name: shared.label_name,
            notes: aiNotes,
            current_description:
              shared.description,
          })) as {
            web_description?: string
            bag_description?: string
          }
        setShared(prev => ({
          ...prev,
          web_description:
            res.web_description ||
            prev.web_description,
          description:
            res.bag_description ||
            prev.description,
        }))
        toast.success(
          'Descriptions written. Check them, edit if needed, then save.'
        )
      } catch (err) {
        toast.error(
          err instanceof Error
            ? err.message
            : 'Failed to write descriptions'
        )
      }
    }

  // AI photo cleanup (Photoroom).
  const cleanupProductPhoto =
    useCleanupProductPhoto()
  const getPhotoOriginals =
    useGetPhotoOriginals()
  const getPhotoCleanupSettings =
    useGetPhotoCleanupSettings()
  const getProductDefaults =
    useGetProductDefaults()
  // New product photo and prices.
  const [newDefaults, setNewDefaults] =
    useState<NewProductDefaults>(
      BUILT_IN_DEFAULTS
    )
  // The price last filled in from the
  // size, so typing a new item name can
  // update it until it's changed by hand.
  const autoPrice1 = useRef('')
  const [autoCleanup, setAutoCleanup] =
    useState(false)
  // cleaned photo URL -> original upload
  const [photoOriginals, setPhotoOriginals] =
    useState<Record<string, string>>({})
  // Photos being cleaned up right now.
  const [cleaningPhotos, setCleaningPhotos] =
    useState<string[]>([])
  // Combine with AI (bag photo + loose popcorn photo).
  const [combineOpen, setCombineOpen] =
    useState(false)
  // Photo shown large (click a thumbnail).
  const [previewPhoto, setPreviewPhoto] =
    useState<string | null>(null)

  const getAvailableSkus =
    useGetAvailableSkus()

  const productOptions =
    useGetProductOptions()

  const options =
    (productOptions.data as
      | {
          categories: string[]
          tags: string[]
          wholesaleTiers?: string[]
        }
      | undefined) ?? {
      categories: [],
      tags: [],
    }

  // Keep a product's current tier selectable
  // even if it was removed from Settings.
  const tierOptions = Array.from(
    new Set([
      ...(options.wholesaleTiers ?? []),
      ...(shared.wholesale_tier
        ? [shared.wholesale_tier]
        : []),
    ])
  )

  // ───────────────────────────────────────────
  // Initialize dialog
  // ───────────────────────────────────────────

  useEffect(() => {
    if (open) {
      const startShared: SharedState =
        product
          ? toShared(product)
          : {
              ...EMPTY_SHARED,
              categories: [
                ...EMPTY_SHARED.categories,
              ],
              tags: [],
              photos: [],
              publish_to: [
                ...DEFAULT_PUBLISH_TO,
              ],
            }
      const startSize1: SizeValues =
        product
          ? toSize(product)
          : { ...EMPTY_SIZE }

      setShared(startShared)
      setSize1(startSize1)
      setSize2({
        ...EMPTY_SIZE,
      })
      setBaseline({
        shared: startShared,
        size1: startSize1,
        size2: { ...EMPTY_SIZE },
        pairMode: false,
      })
      setTab(initialTab)
      setRecipeSeen(initialTab === 'recipe' || initialTab === 'pricing')
      recipe.setConfirmOpen(false)
      setConfirmDiscard(false)
      setAiOpen(false)
      setAiNotes('')

      setPairMode(false)
      setSuggestedSku2('')

      void productOptions.trigger({})

      setCleaningPhotos([])
      setCombineOpen(false)
      setPhotoOriginals({})
      setAutoCleanup(false)
      autoPrice1.current = ''
      void getProductDefaults
        .trigger({}, { skipCache: true })
        .then((res: unknown) => {
          const d = res as
            | Partial<NewProductDefaults>
            | undefined
          const loaded: NewProductDefaults = {
            photo_url: d?.photo_url || null,
            small_price:
              d?.small_price ??
              BUILT_IN_DEFAULTS.small_price,
            large_price:
              d?.large_price ??
              BUILT_IN_DEFAULTS.large_price,
          }
          setNewDefaults(loaded)
          // A new product starts with the
          // default photo (not an unsaved
          // change).
          const photo = loaded.photo_url
          if (!product && photo) {
            const withPhoto = (
              prev: SharedState
            ): SharedState =>
              prev.photos.length === 0
                ? { ...prev, photos: [photo] }
                : prev
            setShared(withPhoto)
            setBaseline(prev =>
              prev
                ? {
                    ...prev,
                    shared: withPhoto(
                      prev.shared
                    ),
                  }
                : prev
            )
          }
        })
        .catch(() =>
          setNewDefaults(BUILT_IN_DEFAULTS)
        )
      void getPhotoCleanupSettings
        .trigger({}, { skipCache: true })
        .then((res: unknown) =>
          setAutoCleanup(
            !!(res as { enabled?: boolean } | undefined)?.enabled
          )
        )
        .catch(() => setAutoCleanup(false))
      const existingPhotos = product
        ? toShared(product).photos
        : []
      if (existingPhotos.length > 0) {
        void getPhotoOriginals
          .trigger({ urls: existingPhotos }, { skipCache: true })
          .then((res: unknown) =>
            setPhotoOriginals(
              (res as { originals?: Record<string, string> } | undefined)
                ?.originals ?? {}
            )
          )
          .catch(() => undefined)
      }

      // For a new product,
      // auto-populate two SKUs.
      if (!product) {
        void getAvailableSkus
          .trigger({
            limit: 2,
          })
          .then((res: unknown) => {
            const skus =
              (
                res as
                  | {
                      skus: string[]
                    }
                  | undefined
              )?.skus ?? []

            // Auto-filled SKUs aren't
            // unsaved changes.
            setBaseline(prev =>
              prev
                ? {
                    ...prev,
                    size1: {
                      ...prev.size1,
                      sku:
                        prev.size1.sku ||
                        skus[0] ||
                        '',
                    },
                    size2: {
                      ...prev.size2,
                      sku:
                        prev.size2.sku ||
                        skus[1] ||
                        '',
                    },
                  }
                : prev
            )

            if (skus[0]) {
              setSize1(prev =>
                prev.sku
                  ? prev
                  : {
                      ...prev,
                      sku: skus[0]!,
                    }
              )
            }

            if (skus[1]) {
              setSuggestedSku2(
                skus[1]!
              )

              setSize2(prev =>
                prev.sku
                  ? prev
                  : {
                      ...prev,
                      sku: skus[1]!,
                    }
              )
            }
          })
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product])

  // ───────────────────────────────────────────
  // Shared setters
  // ───────────────────────────────────────────

  const setSharedField = (
    key:
      | 'label_name'
      | 'description'
      | 'web_description'
      | 'ingredients'
      | 'allergens',
    value: string
  ) =>
    setShared(prev => ({
      ...prev,
      [key]: value,
    }))

  const setList = (
    key: 'categories' | 'tags',
    value: string[]
  ) =>
    setShared(prev => ({
      ...prev,
      [key]: value,
    }))

  const setPublishTo = (
    labels: string[]
  ) => {
    const values =
      labels.map(
        publishLabelToValue
      )

    setShared(prev => ({
      ...prev,
      publish_to: values,
    }))
  }

  // Editing Label Name auto-fills
  // empty item names in pair mode.
  const setLabelName = (
    value: string
  ) => {
    setShared(prev => ({
      ...prev,
      label_name: value,
    }))

    if (pairMode) {
      setSize1(prev => ({
        ...prev,

        item_name:
          prev.item_name
            ? prev.item_name
            : value
              ? `${value} - Small`
              : '',
      }))

      setSize2(prev => ({
        ...prev,

        item_name:
          prev.item_name
            ? prev.item_name
            : value
              ? `${value} - Large`
              : '',
      }))
    }
  }

  // ───────────────────────────────────────────
  // Pair mode
  // ───────────────────────────────────────────

  const togglePairMode = (
    on: boolean
  ) => {
    setPairMode(on)

    if (on) {
      const label =
        shared.label_name

      setSize1(prev => ({
        ...prev,

        item_name:
          prev.item_name ||
          (label
            ? `${label} - Small`
            : ''),

        // Default prices by size.
        price:
          prev.price &&
          prev.price !== autoPrice1.current
            ? prev.price
            : String(
                newDefaults.small_price
              ),
      }))
      autoPrice1.current = String(
        newDefaults.small_price
      )

      setSize2(prev => ({
        ...prev,

        sku:
          prev.sku ||
          suggestedSku2,

        item_name:
          prev.item_name ||
          (label
            ? `${label} - Large`
            : ''),

        price:
          prev.price ||
          String(
            newDefaults.large_price
          ),
      }))
    }
  }

  // ───────────────────────────────────────────
  // Number helper
  // ───────────────────────────────────────────

  const num = (
    v: string
  ): number | null => {
    if (v.trim() === '') {
      return null
    }

    const n = Number(v)

    return isNaN(n)
      ? null
      : n
  }

  // ───────────────────────────────────────────
  // Photos
  // ───────────────────────────────────────────

  const handlePhotoSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files =
      Array.from(
        e.target.files ?? []
      )

    if (files.length === 0) {
      return
    }

    const invalid =
      files.some(
        f =>
          !f.type.startsWith(
            'image/'
          )
      )

    if (invalid) {
      toast.error(
        'Please select image files only'
      )

      if (fileInputRef.current) {
        fileInputRef.current.value =
          ''
      }

      return
    }

    const uploadedUrls: string[] =
      []

    let failures = 0

    for (const file of files) {
      try {
        const base64 =
          await fileToBase64(
            file
          )

        const result =
          await uploadProductPhoto.trigger(
            {
              fileName:
                file.name,

              data:
                base64,

              mimeType:
                file.type,
            }
          )

        const url =
          (
            result as
              | {
                  url: string
                }
              | undefined
          )?.url

        if (url) {
          uploadedUrls.push(url)
        } else {
          failures++
        }
      } catch {
        failures++
      }
    }

    if (
      uploadedUrls.length > 0
    ) {
      setShared(prev => ({
        ...prev,

        // A real photo replaces the
        // default "coming soon" photo.
        photos: [
          ...prev.photos.filter(
            p => p !== newDefaults.photo_url
          ),
          ...uploadedUrls,
        ],
      }))

      if (!autoCleanup) {
        toast.success(
          uploadedUrls.length === 1
            ? 'Photo uploaded'
            : `${uploadedUrls.length} photos uploaded`
        )
      }
    }

    if (failures > 0) {
      toast.error(
        failures === 1
          ? 'Failed to upload a photo'
          : `Failed to upload ${failures} photos`
      )
    }

    if (fileInputRef.current) {
      fileInputRef.current.value =
        ''
    }

    if (autoCleanup) {
      setCleaningPhotos(prev => [
        ...prev,
        ...uploadedUrls,
      ])
      for (const url of uploadedUrls) {
        await handleCleanupPhoto(url)
      }
    }
  }

  // Swap a photo for its cleaned-up version.
  // The product's save syncs the new photo out.
  const handleCleanupPhoto = async (
    url: string
  ) => {
    setCleaningPhotos(prev =>
      prev.includes(url)
        ? prev
        : [...prev, url]
    )
    try {
      const result =
        (await cleanupProductPhoto.trigger(
          { url }
        )) as
          | {
              url: string
              originalUrl: string
            }
          | undefined
      if (!result?.url) {
        throw new Error(
          'Photo cleanup failed'
        )
      }
      setShared(prev => ({
        ...prev,
        photos: prev.photos.map(p =>
          p === url ? result.url : p
        ),
      }))
      setPhotoOriginals(prev => ({
        ...prev,
        [result.url]:
          result.originalUrl,
      }))
      toast.success(
        'Photo cleaned up. Click it to check it against the original.'
      )
    } catch (err) {
      toast.error(
        `${
          err instanceof Error
            ? err.message
            : 'Photo cleanup failed'
        } The original photo was kept.`
      )
    } finally {
      setCleaningPhotos(prev =>
        prev.filter(p => p !== url)
      )
    }
  }

  // Add the Combine with AI photo; featured puts it first.
  const handleAddCombined = (
    url: string,
    featured: boolean
  ) => {
    setShared(prev => {
      const rest = prev.photos.filter(
        p => p !== newDefaults.photo_url
      )
      return {
        ...prev,
        photos: featured
          ? [url, ...rest]
          : [...rest, url],
      }
    })
  }

  // Put the original upload back.
  const handleUseOriginal = (
    url: string
  ) => {
    const original =
      photoOriginals[url]
    if (!original) return
    setShared(prev => ({
      ...prev,
      photos: prev.photos.map(p =>
        p === url ? original : p
      ),
    }))
  }

  const handleSetFeatured = (
    url: string
  ) => {
    setShared(prev => {
      const rest =
        prev.photos.filter(
          p => p !== url
        )

      return {
        ...prev,
        photos: [
          url,
          ...rest,
        ],
      }
    })
  }

  const handleRemovePhoto = (
    url: string
  ) => {
    setShared(prev => ({
      ...prev,

      photos:
        prev.photos.filter(
          p => p !== url
        ),
    }))
  }

  // ───────────────────────────────────────────
  // Build backend payload
  // ───────────────────────────────────────────

  const buildPayload = (
    size: SizeValues
  ) => ({
    sku:
      size.sku.trim(),

    item_name:
      size.item_name.trim() ||
      null,

    label_name:
      shared.label_name.trim() ||
      null,

    weight_oz:
      num(size.weight_oz),

    // Blank clears it (production and
    // costs fall back to Weight).
    actual_weight_oz:
      num(size.actual_weight_oz),

    price:
      num(size.price),

    qty_in_stock:
      num(size.qty_in_stock),

    qty_remake:
      num(size.qty_remake),

    description:
      shared.description.trim() ||
      null,

    web_description:
      shared.web_description.trim(),

    ingredients:
      shared.ingredients ||
      null,

    allergens:
      shared.allergens ||
      null,

    image_url:
      shared.photos[0] ??
      null,

    photos:
      shared.photos,

    categories:
      shared.categories,

    tags:
      shared.tags,

    publish_to:
      shared.publish_to,

    is_seasonal:
      shared.is_seasonal,

    available_start:
      shared.is_seasonal
        ? shared.available_start
        : null,

    available_end:
      shared.is_seasonal
        ? shared.available_end
        : null,

    wholesale_tier:
      shared.wholesale_tier,

    // Only Special Order flavors keep an
    // unbagged batch; others leave it alone.
    ...(isSpecialOrder(shared.wholesale_tier)
      ? {
          unbagged_oz: num(
            shared.unbagged_oz
          ),
        }
      : {}),
  })

  // ───────────────────────────────────────────
  // Save
  // ───────────────────────────────────────────

  const handleSubmit =
    async () => {
      // Show the section with the problem.
      const fail = (
        section: Section,
        message: string
      ) => {
        setTab(section)
        toast.error(message)
      }

      if (recipeDirty) {
        // The recipe belongs to the saved
        // Label Name.
        if (labelRenamed) {
          fail(
            'recipe',
            'Save the new Label Name first, then change the recipe.'
          )
          return
        }
        const problem =
          recipe.checkSave()
        if (problem) {
          fail('recipe', problem)
          return
        }
      }

      // Only the recipe changed: the
      // product doesn't need saving.
      if (!productDirty) {
        startRecipeSave()
        return
      }

      // Shared required fields

      if (
        !shared.label_name.trim()
      ) {
        fail(
          'details',
          'Label Name is required'
        )

        return
      }

      if (
        !shared.description.trim()
      ) {
        fail(
          'details',
          'Bag description is required'
        )

        return
      }

      if (shared.is_seasonal) {
        if (
          !shared.available_start ||
          !shared.available_end
        ) {
          fail(
          'channels',
            'Availability Start and End are required for seasonal products'
          )

          return
        }

        if (
          shared.available_end <
          shared.available_start
        ) {
          fail(
          'channels',
            'Availability End must be on or after Availability Start'
          )

          return
        }
      }

      // First product / Small

      if (!size1.sku.trim()) {
        fail(
          'details',
          'SKU is required'
        )

        return
      }

      if (
        !size1.item_name.trim()
      ) {
        fail(
          'details',
          pairMode
            ? 'Small Item Name is required'
            : 'Item Name is required'
        )

        return
      }

      if (!size1.price.trim()) {
        fail(
          'pricing',
          pairMode
            ? 'Small Price is required'
            : 'Price is required'
        )

        return
      }

      const price1 =
        Number(size1.price)

      if (
        !Number.isFinite(
          price1
        ) ||
        price1 < 0
      ) {
        fail(
          'pricing',
          pairMode
            ? 'Small Price must be a valid amount'
            : 'Price must be a valid amount'
        )

        return
      }

      if (
        !size1.weight_oz.trim()
      ) {
        fail(
          'details',
          pairMode
            ? 'Small Weight is required'
            : 'Weight is required'
        )

        return
      }

      const weight1 =
        Number(
          size1.weight_oz
        )

      if (
        !Number.isFinite(
          weight1
        ) ||
        weight1 <= 0
      ) {
        fail(
          'details',
          pairMode
            ? 'Small Weight must be greater than 0'
            : 'Weight must be greater than 0'
        )

        return
      }

      const badActual = (
        size: SizeValues
      ) => {
        const v =
          size.actual_weight_oz.trim()

        if (!v) return false

        const n = Number(v)

        return (
          !Number.isFinite(n) ||
          n <= 0
        )
      }

      if (badActual(size1)) {
        fail(
          'details',
          pairMode
            ? 'Small Actual Weight must be greater than 0'
            : 'Actual Weight must be greater than 0'
        )

        return
      }

      if (
        !isEdit &&
        pairMode &&
        badActual(size2)
      ) {
        fail(
          'details',
          'Large Actual Weight must be greater than 0'
        )

        return
      }

      // Second product / Large

      const addPair =
        !isEdit &&
        pairMode

      if (addPair) {
        if (
          !size2.sku.trim()
        ) {
          fail(
          'details',
            'The Large product needs its own SKU'
          )

          return
        }

        if (
          !size2.item_name.trim()
        ) {
          fail(
          'details',
            'Large Item Name is required'
          )

          return
        }

        if (
          !size2.price.trim()
        ) {
          fail(
          'pricing',
            'Large Price is required'
          )

          return
        }

        const price2 =
          Number(
            size2.price
          )

        if (
          !Number.isFinite(
            price2
          ) ||
          price2 < 0
        ) {
          fail(
          'pricing',
            'Large Price must be a valid amount'
          )

          return
        }

        if (
          !size2.weight_oz.trim()
        ) {
          fail(
          'details',
            'Large Weight is required'
          )

          return
        }

        const weight2 =
          Number(
            size2.weight_oz
          )

        if (
          !Number.isFinite(
            weight2
          ) ||
          weight2 <= 0
        ) {
          fail(
          'details',
            'Large Weight must be greater than 0'
          )

          return
        }

        if (
          size1.sku.trim() ===
          size2.sku.trim()
        ) {
          fail(
          'details',
            'The two sizes must have different SKUs'
          )

          return
        }
      }

      if (recipeDirty) {
        startRecipeSave()
        return
      }
      await finishSave(null)
    }

  // A recipe change saves straight away, or
  // first asks whether to update the
  // products' ingredients (Save recipe only
  // / Save & update products).
  const startRecipeSave = () => {
    const mode = recipe.saveMode()
    if (mode === 'confirm') {
      recipe.setConfirmOpen(true)
    } else {
      void finishSave({
        apply: mode === 'changedOnly',
        changedOnly: mode === 'changedOnly',
      })
    }
  }

  // Saves the product (when it changed),
  // then the recipe (when given), then
  // closes and publishes the flavors whose
  // ingredient lists changed.
  const finishSave = async (
    recipeSave: {
      apply: boolean
      changedOnly: boolean
    } | null
  ) => {
    setSavingAll(true)
    try {
      let productSaved = false
      if (productDirty) {
        if (!(await saveProduct())) return
        productSaved = true
      }
      if (recipeSave) {
        const res = await recipe.save(
          recipeSave.apply,
          recipeSave.changedOnly
        )
        if (!res) {
          // The product went through; keep
          // the dialog open on the recipe.
          if (productSaved) {
            setBaseline({
              shared,
              size1,
              size2,
              pairMode,
            })
            onSaved()
          }
          setTab('recipe')
          return
        }
        void publishLabels(
          res.publishLabels ?? [],
          params =>
            publishLabelProducts.trigger(
              params
            )
        )
      }
      recipe.setConfirmOpen(false)
      onOpenChange(false)
      onSaved()
    } finally {
      setSavingAll(false)
    }
  }

  const saveProduct =
    async (): Promise<boolean> => {
      const addPair =
        !isEdit &&
        pairMode

      try {
        const results: unknown[] = [
          await upsertProduct.trigger(
            buildPayload(size1)
          ),
        ]

        if (addPair) {
          results.push(
            await upsertProduct.trigger(
              buildPayload(size2)
            )
          )
        }

        const saved =
          isEdit
            ? 'Product updated'
            : addPair
              ? 'Both products added'
              : 'Product added'

        // Say where it went, and which
        // destinations failed or were
        // skipped, instead of a plain
        // success when nothing synced.
        const reached = new Set<string>()
        const problems: SyncProblem[] = []
        for (const res of results) {
          const row = (
            Array.isArray(res)
              ? res[0]
              : res
          ) as
            | { sync_results?: unknown }
            | undefined
          const r = readSyncResults(
            row?.sync_results
          )
          r.reached.forEach(d =>
            reached.add(d)
          )
          problems.push(...r.problems)
        }

        if (
          shared.publish_to.length === 0
        ) {
          toast.warning(
            `${saved} in the Hub only. Nothing is ticked under Publish To.`,
            { duration: 15000 }
          )
        } else if (problems.length > 0) {
          const ok = DESTINATION_ORDER.filter(
            d => reached.has(d)
          ).map(destinationName)
          toast[
            problems.some(
              p => p.status === 'failed'
            )
              ? 'error'
              : 'warning'
          ](
            `${saved}` +
              (ok.length > 0
                ? ` and synced to ${listNames(ok)}.`
                : '.') +
              ' ' +
              describeProblems(problems),
            { duration: 20000 }
          )
        } else {
          const ok = DESTINATION_ORDER.filter(
            d => reached.has(d)
          ).map(destinationName)
          toast.success(
            ok.length > 0
              ? `${saved} and synced to ${listNames(ok)}`
              : saved
          )
        }

        return true
      } catch {
        toast.error(
          'Failed to save product'
        )
        return false
      }
    }

  const uploading =
    uploadProductPhoto.loading

  const cleaning =
    cleaningPhotos.length > 0

  const saving =
    savingAll ||
    upsertProduct.loading ||
    recipe.saving

  const productChanged = changedSections(
    { shared, size1, size2, pairMode },
    baseline
  )
  const recipeDirty =
    isEdit && recipe.dirty
  // Save Changes re-saves an unchanged
  // product (re-syncs it), unless only the
  // recipe changed.
  const productDirty =
    !isEdit ||
    !recipeDirty ||
    productChanged.length > 0
  // A Label Name edited but not saved yet.
  const labelRenamed =
    isEdit &&
    shared.label_name.trim() !==
      (product?.label_name ?? '').trim()
  const changed: Section[] =
    recipeDirty &&
    !productChanged.includes('recipe')
      ? SECTIONS.map(s => s.value).filter(
          v =>
            v === 'recipe' ||
            productChanged.includes(v)
        )
      : productChanged
  const dirty = changed.length > 0

  // Closing (Cancel, X, Escape or a click
  // outside) asks first when something
  // hasn't been saved.
  const requestClose = () => {
    if (dirty && !saving) {
      setConfirmDiscard(true)
    } else {
      onOpenChange(false)
    }
  }

  // Warn before leaving the page too.
  useEffect(() => {
    if (!open || !dirty) return
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener(
      'beforeunload',
      warn
    )
    return () =>
      window.removeEventListener(
        'beforeunload',
        warn
      )
  }, [open, dirty])

  const skuHelper = (
    idx: 1 | 2
  ) =>
    !isEdit ? (
      <p className="text-xs text-muted-foreground">
        {getAvailableSkus.loading
          ? 'Finding an available SKU…'
          : (
                idx === 1
                  ? size1.sku
                  : size2.sku
              )
            ? 'Auto-filled from the available SKU pool. You can override it.'
            : 'No available SKU in the pool — enter one manually.'}
      </p>
    ) : null

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onOpenChange={o =>
        o
          ? onOpenChange(true)
          : requestClose()
      }
    >
      <DialogContent
        className={cn(
          // Fixed height so switching tabs
          // doesn't resize the dialog; the
          // tab body scrolls and Save stays put.
          'max-h-[90vh] sm:h-[min(90vh,760px)]',
          pairMode
            ? 'max-w-4xl'
            : 'max-w-2xl'
        )}
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? 'Edit Product'
              : 'Add Product'}
          </DialogTitle>

          <DialogDescription>
            {isEdit
              ? 'Update the details for this product.'
              : 'Enter the details for the new product.'}
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={tab}
          onValueChange={v => {
            setTab(v as Section)
            if (v === 'recipe' || v === 'pricing')
              setRecipeSeen(true)
          }}
          className="flex min-h-0 flex-1 flex-col gap-3"
        >
          <TabsList className="h-auto w-full shrink-0 flex-wrap justify-start gap-1 sm:flex-nowrap">
            {SECTIONS.map(s => (
              <TabsTrigger
                key={s.value}
                value={s.value}
                className="gap-1.5"
              >
                {s.label}
                {changed.includes(
                  s.value
                ) && (
                  <span
                    className="h-1.5 w-1.5 rounded-full bg-primary"
                    aria-label="Unsaved changes"
                    title="Unsaved changes"
                  />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <DialogBody>
            <TabsContent
              value="details"
              className="mt-0 space-y-4 pb-2"
            >
              {/* Photos */}

              <div className="space-y-1.5">
                <Label>
                  Product Photos
                </Label>

                <p className="text-xs text-muted-foreground"><HelpText>
                  Upload one or more photos.
                  Click the star to set the
                  featured photo shown in the
                  product list. Small and Large
                  share the same photos.
                  {autoCleanup
                    ? ' New photos are cleaned up with ChatGPT (about 35 seconds each). Click a photo to compare it with the original; hover and click the undo arrow to put the original back.'
                    : ' Hover a photo and click the sparkles to clean it up with ChatGPT.'}
                  {' Combine with AI makes one photo of the bag with loose popcorn in front, from a bag photo and a popcorn photo.'}
                </HelpText></p>

                {shared.photos.length >
                  0 && (
                  <div className="flex flex-wrap gap-3">
                    {shared.photos.map(
                      (url, idx) => {
                        const featured =
                          idx === 0
                        const isCleaning =
                          cleaningPhotos.includes(
                            url
                          )
                        const isCleaned =
                          !!photoOriginals[url]

                        return (
                          <ProductPhotoTile
                            key={url}
                            url={url}
                            featured={featured}
                            isCleaning={isCleaning}
                            isCleaned={isCleaned}
                            onPreview={() => setPreviewPhoto(url)}
                            onSetFeatured={() => handleSetFeatured(url)}
                            onRemove={() => handleRemovePhoto(url)}
                            onCleanup={() => void handleCleanupPhoto(url)}
                            onUseOriginal={() => handleUseOriginal(url)}
                          />
                        )
                      }
                    )}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  {shared.photos.length ===
                    0 && (
                    <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
                      <ImageIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={e =>
                      void handlePhotoSelect(
                        e
                      )
                    }
                  />

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={
                      uploading ||
                      cleaning
                    }
                    onClick={() =>
                      fileInputRef.current?.click()
                    }
                  >
                    {uploading || cleaning ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}

                    {uploading
                      ? 'Uploading…'
                      : cleaning
                        ? 'Cleaning up…'
                        : 'Upload Photos'}
                  </Button>

                  {shared.photos.length >= 2 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={
                        uploading ||
                        cleaning
                      }
                      onClick={() =>
                        setCombineOpen(true)
                      }
                    >
                      <Layers className="h-4 w-4" />
                      Combine with AI
                    </Button>
                  )}
                </div>
              </div>

              {/* Label Name */}

              <div className="space-y-1.5">
                <Label htmlFor="pf-label-name">
                  Label Name{' '}
                  <span className="text-destructive">
                    *
                  </span>
                </Label>

                <Input
                  id="pf-label-name"
                  value={
                    shared.label_name
                  }
                  onChange={e =>
                    setLabelName(
                      e.target.value
                    )
                  }
                />

                <p className="text-xs text-muted-foreground">
                  Shared across sizes and used
                  to key the recipe.
                </p>
              </div>
          
              {/* Pair mode */}

              {!isEdit && (
                <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2.5">
                  <div>
                    <Label
                      htmlFor="pf-pair-mode"
                      className="text-sm font-medium"
                    >
                      Add Small &amp; Large at
                      once
                    </Label>

                    <p className="text-xs text-muted-foreground">
                      Create two products that
                      share the label, photos,
                      publishing destinations,
                      and details.
                    </p>
                  </div>

                  <Switch
                    id="pf-pair-mode"
                    checked={
                      pairMode
                    }
                    onCheckedChange={
                      togglePairMode
                    }
                  />
                </div>
              )}

              {pairMode ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:divide-x">
                  <div className="sm:pr-6">
                    <SizeFieldGroup
                      idPrefix="pf-s1"
                      title="Small"
                      part="details"
                      values={size1}
                      onChange={(k, v) =>
                        setSize1(prev => ({
                          ...prev,
                          [k]: v,
                        }))
                      }
                      skuHelper={skuHelper(1)}
                    />
                  </div>

                  <div className="sm:pl-6">
                    <SizeFieldGroup
                      idPrefix="pf-s2"
                      title="Large"
                      part="details"
                      values={size2}
                      onChange={(k, v) =>
                        setSize2(prev => ({
                          ...prev,
                          [k]: v,
                        }))
                      }
                      skuHelper={skuHelper(2)}
                    />
                  </div>
                </div>
              ) : (
                <SizeFieldGroup
                  idPrefix="pf"
                  part="details"
                  values={size1}
                  onChange={(k, v) =>
                    setSize1(prev => {
                      const next = {
                        ...prev,
                        [k]: v,
                      }
                      // A new product's price
                      // follows its size until
                      // typed in by hand.
                      if (
                        !isEdit &&
                        k === 'item_name' &&
                        (prev.price === '' ||
                          prev.price ===
                            autoPrice1.current)
                      ) {
                        const price =
                          defaultPriceFor(
                            v,
                            newDefaults
                          )
                        if (price) {
                          next.price = price
                          autoPrice1.current =
                            price
                        }
                      }
                      return next
                    })
                  }
                  skuDisabled={isEdit}
                  skuHelper={skuHelper(1)}
                />
              )}

              {/* Write with AI */}

              <div className="space-y-2 rounded-md border border-dashed p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">
                      Write descriptions with AI
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Describe the flavor in your own words and ChatGPT writes the web and bag descriptions below.
                    </p>
                  </div>
                  {!aiOpen && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      onClick={() => {
                        setAiNotes(
                          prev =>
                            prev ||
                            shared.description
                        )
                        setAiOpen(true)
                      }}
                    >
                      <Sparkles className="h-4 w-4" />
                      Write with AI
                    </Button>
                  )}
                </div>

                {aiOpen && (
                  <div className="space-y-2">
                    <Label htmlFor="pf-ai-notes">
                      About the flavor
                    </Label>
                    <Textarea
                      id="pf-ai-notes"
                      value={aiNotes}
                      onChange={e =>
                        setAiNotes(
                          e.target.value
                        )
                      }
                      rows={3}
                      placeholder="e.g. sweet kettle corn with real strawberry and banana, tastes like a smoothie"
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setAiOpen(false)
                        }
                        disabled={
                          writeDescriptions.loading
                        }
                      >
                        Close
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="gap-1.5"
                        onClick={() =>
                          void runWriteDescriptions()
                        }
                        disabled={
                          writeDescriptions.loading ||
                          !aiNotes.trim()
                        }
                      >
                        {writeDescriptions.loading ? (
                          <Spinner className="h-4 w-4" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                        {writeDescriptions.loading
                          ? 'Writing…'
                          : shared.web_description.trim()
                            ? 'Rewrite'
                            : 'Write descriptions'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground"><HelpText>
                      This replaces both descriptions below. Nothing is saved until you click Save. Prompts are in Settings &gt; AI tools &gt; AI descriptions.
                    </HelpText></p>
                  </div>
                )}
              </div>

              {/* Bag description */}

              <div className="space-y-1.5">
                <div className="flex items-baseline justify-between gap-2">
                  <Label htmlFor="pf-description">
                    Bag description{' '}
                    <span className="text-destructive">
                      *
                    </span>
                  </Label>
                  <span className="text-xs text-muted-foreground">
                    {shared.description.trim().length} characters
                  </span>
                </div>

                <Textarea
                  id="pf-description"
                  value={
                    shared.description
                  }
                  onChange={e =>
                    setSharedField(
                      'description',
                      e.target.value
                    )
                  }
                  rows={2}
                />
                <p className="text-xs text-muted-foreground">
                  Short, printed on the bag label. Also used for Zoho and as the store's short description.
                </p>
              </div>

              {/* Web description */}

              <div className="space-y-1.5">
                <Label htmlFor="pf-web-description">
                  Web description
                </Label>

                <Textarea
                  id="pf-web-description"
                  value={
                    shared.web_description
                  }
                  onChange={e =>
                    setSharedField(
                      'web_description',
                      e.target.value
                    )
                  }
                  rows={6}
                />
                <p className="text-xs text-muted-foreground">
                  Longer description for the B2B and Web stores. Leave blank to use the bag description there.
                </p>
              </div>

              {/* Ingredients */}

              <div className="space-y-1.5">
                <Label htmlFor="pf-ingredients">
                  Ingredients
                </Label>
                <p className="text-xs text-muted-foreground"><HelpText>
                  Printed on the label and sent to the stores. Saving a recipe
                  with Save &amp; update products fills these in from the Recipe tab.
                </HelpText></p>

                <Textarea
                  id="pf-ingredients"
                  value={
                    shared.ingredients
                  }
                  onChange={e =>
                    setSharedField(
                      'ingredients',
                      e.target.value
                    )
                  }
                  rows={2}
                />
                {(shared.ingredients ?? '').length > 0 ? (
                  <p
                    className={`text-xs tabular-nums ${
                      shared.ingredients.length > LABEL_INGREDIENTS_MAX
                        ? 'font-medium text-destructive'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {shared.ingredients.length.toLocaleString()} / {LABEL_INGREDIENTS_MAX} characters
                    {shared.ingredients.length > LABEL_INGREDIENTS_MAX ? ' · too long for the label' : ''}
                  </p>
                ) : null}
              </div>

              {/* Allergens */}

              <div className="space-y-1.5">
                <Label htmlFor="pf-allergens">
                  Allergens
                </Label>

                <Textarea
                  id="pf-allergens"
                  value={
                    shared.allergens
                  }
                  onChange={e =>
                    setSharedField(
                      'allergens',
                      e.target.value
                    )
                  }
                  rows={2}
                />
              </div>

            </TabsContent>

            <TabsContent
              value="pricing"
              className="mt-0 space-y-4 pb-2"
            >
              {pairMode ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:divide-x">
                  <div className="sm:pr-6">
                    <SizeFieldGroup
                      idPrefix="pf-s1"
                      title="Small"
                      part="pricing"
                      values={size1}
                      onChange={(k, v) =>
                        setSize1(prev => ({
                          ...prev,
                          [k]: v,
                        }))
                      }
                    />
                  </div>

                  <div className="sm:pl-6">
                    <SizeFieldGroup
                      idPrefix="pf-s2"
                      title="Large"
                      part="pricing"
                      values={size2}
                      onChange={(k, v) =>
                        setSize2(prev => ({
                          ...prev,
                          [k]: v,
                        }))
                      }
                    />
                  </div>
                </div>
              ) : (
                <SizeFieldGroup
                  idPrefix="pf"
                  part="pricing"
                  values={size1}
                  onChange={(k, v) =>
                    setSize1(prev => ({
                      ...prev,
                      [k]: v,
                    }))
                  }
                  skuDisabled={isEdit}
                />
              )}

              {/* Wholesale Tier */}

              <div className="space-y-1.5">
                <Label>
                  Wholesale Tier
                </Label>

                <Select
                  value={
                    shared.wholesale_tier ||
                    NO_TIER
                  }
                  onValueChange={v =>
                    setShared(prev => ({
                      ...prev,
                      wholesale_tier:
                        v === NO_TIER
                          ? prev.wholesale_tier ==
                            null
                            ? null
                            : ''
                          : v,
                    }))
                  }
                >
                  <SelectTrigger className="h-9 w-full sm:w-1/2">
                    <SelectValue placeholder="No tier" />
                  </SelectTrigger>

                  <SelectContent>
                    <SelectItem value={NO_TIER}>
                      No tier
                    </SelectItem>

                    {tierOptions.map(tier => (
                      <SelectItem
                        key={tier}
                        value={tier}
                      >
                        {tier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Unbagged batch (Special Order) */}

              {isSpecialOrder(shared.wholesale_tier) && (
                <div className="space-y-1.5">
                  <Label htmlFor="pf-unbagged">
                    Unbagged popcorn (oz)
                  </Label>

                  <Input
                    id="pf-unbagged"
                    type="number"
                    min="0"
                    step="any"
                    className="h-9 w-full sm:w-1/2"
                    value={shared.unbagged_oz}
                    onChange={e =>
                      setShared(prev => ({
                        ...prev,
                        unbagged_oz: e.target.value,
                      }))
                    }
                  />

                  <p className="text-xs text-muted-foreground">
                    {batchFillText(
                      shared.unbagged_oz,
                      pairMode
                        ? [
                            { name: 'Small', size: size1 },
                            { name: 'Large', size: size2 },
                          ]
                        : [{ name: 'bags', size: size1 }]
                    )}
                  </p>
                </div>
              )}

              {/* Cost & margins, from the recipe */}

              {isEdit && recipe.hasLabel ? (
                recipe.recipeLoading ? (
                  <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
                    Loading cost…
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    <RecipeCostSummary editor={recipe} />
                    <p className="text-xs text-muted-foreground">
                      From the recipe and this product&apos;s saved price and weight.
                      Edit the recipe on the Recipe tab.
                    </p>
                  </div>
                )
              ) : null}

            </TabsContent>

            <TabsContent
              value="recipe"
              className="mt-0 space-y-4 pb-2"
            >
              {/* Recipe */}

              {isEdit ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {recipeDescription(recipe)}
                  </p>
                  {labelRenamed && (
                    <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
                      The Label Name was changed on
                      Details. This is still the recipe
                      for “{recipe.labelName}”. Save the
                      new name first, then change the
                      recipe.
                    </p>
                  )}
                  <RecipeEditorFields
                    editor={recipe}
                    saveLabel="Save Changes"
                    embedded
                    showCost={false}
                  />
                </>
              ) : (
                <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground"><HelpText>
                  Add the product first. Then
                  open it again to set up its
                  recipe (recipes are shared by
                  Label Name, so every size uses
                  the same one).
                </HelpText></p>
              )}

            </TabsContent>

            <TabsContent
              value="channels"
              className="mt-0 space-y-4 pb-2"
            >
              {/* Publish To */}

              <div className="space-y-1.5">
                <Label>
                  Publish To
                </Label>

                <MultiSelect
                  options={PUBLISH_OPTIONS}
                  value={shared.publish_to.map(
                    publishValueToLabel
                  )}
                  onChange={setPublishTo}
                  placeholder="Select publishing destinations…"
                  searchPlaceholder="Search destinations…"
                />

                <p className="text-xs text-muted-foreground">
                  Controls which connected systems receive this product.
                  This setting is shared across Small and Large.
                </p>
              </div>

              {/* Categories & Tags */}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>
                    Categories
                  </Label>

                  <MultiSelect
                    options={
                      options.categories
                    }
                    value={
                      shared.categories
                    }
                    onChange={v =>
                      setList(
                        'categories',
                        v
                      )
                    }
                    placeholder="Select categories…"
                    searchPlaceholder="Search categories…"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>
                    Tags
                  </Label>

                  <MultiSelect
                    options={
                      options.tags
                    }
                    value={
                      shared.tags
                    }
                    onChange={v =>
                      setList(
                        'tags',
                        v
                      )
                    }
                    placeholder="Select tags…"
                    searchPlaceholder="Search tags…"
                  />
                </div>
              </div>

              {/* Seasonal availability */}

              <div className="space-y-3 rounded-md border p-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="pf-seasonal"
                      className="text-sm font-medium"
                    >
                      Seasonal product
                    </Label>

                    <p className="text-xs text-muted-foreground">
                      Syncs the availability dates
                      to B2B and Web, repeating
                      every year.
                    </p>
                  </div>

                  <Switch
                    id="pf-seasonal"
                    checked={
                      shared.is_seasonal ===
                      true
                    }
                    onCheckedChange={checked =>
                      setShared(prev => ({
                        ...prev,
                        is_seasonal: checked,
                      }))
                    }
                  />
                </div>

                {shared.is_seasonal && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="pf-available-start">
                        Availability Start{' '}
                        <span className="text-destructive">
                          *
                        </span>
                      </Label>

                      <DatePicker
                        id="pf-available-start"
                        value={
                          shared.available_start
                        }
                        placeholder="Pick start date"
                        onChange={v =>
                          setShared(prev => ({
                            ...prev,
                            available_start: v,
                          }))
                        }
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="pf-available-end">
                        Availability End{' '}
                        <span className="text-destructive">
                          *
                        </span>
                      </Label>

                      <DatePicker
                        id="pf-available-end"
                        value={
                          shared.available_end
                        }
                        defaultMonth={shared.available_start}
                        placeholder="Pick end date"
                        onChange={v =>
                          setShared(prev => ({
                            ...prev,
                            available_end: v,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </div>

            </TabsContent>
          </DialogBody>
        </Tabs>

        <DialogFooter className="items-center gap-2 border-t pt-3">
          {dirty && (
            <p className="text-xs text-muted-foreground sm:mr-auto">
              Unsaved changes in{' '}
              {changed
                .map(
                  c =>
                    SECTIONS.find(
                      x => x.value === c
                    )?.label
                )
                .join(', ')}
            </p>
          )}

          <Button
            variant="outline"
            onClick={requestClose}
            disabled={
              saving
            }
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              void handleSubmit()
            }
            disabled={
              saving ||
              uploading ||
              cleaning
            }
          >
            {saving
              ? 'Saving…'
              : isEdit
                ? 'Save Changes'
                : pairMode
                  ? 'Add Both'
                  : 'Add Product'}
          </Button>
        </DialogFooter>

        {/* Recipe save: update the products'
            ingredients too? */}
        <RecipeSaveConfirm
          editor={recipe}
          saving={saving}
          onSave={apply =>
            void finishSave({
              apply,
              changedOnly: false,
            })
          }
        />

        {/* A nested Dialog, not AlertDialog:
            the two use different Radix copies
            and fight over focus. */}
        <Dialog
          open={confirmDiscard}
          onOpenChange={setConfirmDiscard}
        >
          <DialogContent
            role="alertdialog"
            className="sm:max-w-md"
          >
            <DialogHeader>
              <DialogTitle>
                Discard unsaved changes?
              </DialogTitle>
              <DialogDescription>
                {isEdit
                  ? "Your changes to this product haven't been saved."
                  : "This product hasn't been added yet."}{' '}
                Changed:{' '}
                {changed
                  .map(
                    c =>
                      SECTIONS.find(
                        x => x.value === c
                      )?.label
                  )
                  .join(', ')}
                .
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  setConfirmDiscard(false)
                }
              >
                Keep editing
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setConfirmDiscard(false)
                  onOpenChange(false)
                }}
              >
                Discard changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <CombinePhotosDialog
          open={combineOpen}
          onOpenChange={setCombineOpen}
          photos={shared.photos}
          onAdd={handleAddCombined}
        />

        <Dialog
          open={!!previewPhoto}
          onOpenChange={o => {
            if (!o) setPreviewPhoto(null)
          }}
        >
          <DialogContent className="max-h-[95vh] overflow-y-auto sm:max-w-4xl">
            <DialogHeader>
              <DialogTitle>
                Product Photo
              </DialogTitle>
              {previewPhoto &&
                photoOriginals[
                  previewPhoto
                ] && (
                  <DialogDescription>
                    Original on the
                    left, cleaned up on
                    the right.
                  </DialogDescription>
                )}
            </DialogHeader>
            {previewPhoto &&
              (photoOriginals[
                previewPhoto
              ] ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {[
                    {
                      label: 'Original',
                      src: photoOriginals[
                        previewPhoto
                      ]!,
                    },
                    {
                      label: 'Cleaned up',
                      src: previewPhoto,
                    },
                  ].map(p => (
                    <figure
                      key={p.label}
                      className="space-y-1.5"
                    >
                      <img
                        src={p.src}
                        alt={p.label}
                        className="max-h-[70vh] w-full rounded-md border bg-muted object-contain"
                      />
                      <figcaption className="text-center text-xs text-muted-foreground">
                        {p.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : (
                <img
                  src={previewPhoto}
                  alt="Product"
                  className="max-h-[75vh] w-full rounded-md border bg-muted object-contain"
                />
              ))}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  )
}